import { Component } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Post } from "utils/axios";
import { handleErrorMsg, thousandSeperatorFormatter } from "utils";
import { requestError, requestSuccess } from "utils/requestHandler";
import { addLoadingContent, removeLoadingContent } from "reducers/LoadingOverlay";
import { PDFGenerator } from "utils/pdf-generator";
import { exportToCSVWithFormatting } from "utils/csv-export";
import { getTranslatedContent } from "utils/utility";
import { orderStatusCode } from "constants";

const HOC = (WrappedComponent) => {
  class WithHOC extends Component {
    state = {
      query: {
        pageNumber: 1, pageSize: 10,
        // agentCode_Name_IC: this.props.profile.Role !== 'AD' ? this.props.profile.MemberId : undefined, 
        salesType: [9],
      },
      orders: [],
      totalOrders: {},
      selectedOrder: {},
      showAddAuthRepModal: false,
      showDetailModal: false
    };

    load = (param) => {
      if (param) {
        this.props.addLoadingContent();
      } else {
        this.props.removeLoadingContent();
      }
    };
    onChangeHOC = (key, val) => this.setState({ [key]: val });

    getOrders = (query) => Post("/sales/getOrderList", query, this.getOrdersSuccess, this.getOrdersError, this.load);
    getOrdersSuccess = (payload) => this.setState({ orders: payload.Data, totalOrders: payload.Total });
    getOrdersError = (error) => { };

    exportOrderListToCSV = (query) => {
      // Create export query with hardcoded pagination
      const exportQuery = {
        ...query,
        pageNumber: 1,
        pageSize: 1000000
      };
      Post(`/sales/getOrderList`, exportQuery, this.exportOrderListToCSVSuccess, this.exportOrderListToCSVError, this.load);
    };
    exportOrderListToCSVSuccess = (payload) => {
      try {
        // Get the order data from the API response
        const orderData = payload?.Data || [];

        if (orderData.length === 0) {
          requestError("No data to export");
          return;
        }

        // Define column configuration with formatting
        const columnConfig = [
          {
            header: getTranslatedContent("{{NUMBER}}"),
            key: "index",
            format: "number",
          },
          {
            header: getTranslatedContent("{{LOT_NO}}"),
            key: "UniqueCode",
            format: "text",
          },
          {
            header: getTranslatedContent("{{PO_NO}}"),
            key: "SalesID",
            format: "text",
          },
          {
            header: getTranslatedContent("{{PURCHASER_NAME}}"),
            key: "Fullname",
            format: "text",
          },
          {
            header: getTranslatedContent("{{AGENT_ID}}"),
            key: "AgentID",
            format: "text",
          },
          {
            header: getTranslatedContent("{{SALES_DATE}}"),
            key: "SalesDate",
            format: "text",
          },
          {
            header: getTranslatedContent("{{RESERVATION_DATE}}"),
            key: "ReservationDate",
            format: "text",
          },
          {
            header: getTranslatedContent("{{UNIT_PRICE}}"),
            key: "UnitPrice",
            format: "float",
          },
          {
            header: getTranslatedContent("{{MAINTENANCE_FEE}}"),
            key: "MaintenanceFee",
            format: "float",
          },
          {
            header: getTranslatedContent("{{DISCOUNT}} (RM)"),
            key: "UnitDiscount",
            format: "float",
          },
          {
            header: getTranslatedContent("{{TOTAL_PRICE}} (RM)"),
            key: "TotalPrice",
            format: "float",
          },
          {
            header: getTranslatedContent("{{UPDATED_BY}}"),
            key: "UpdatedBy",
            format: "text",
          },
          {
            header: getTranslatedContent("{{INTRODUCER_ID}}"),
            key: "IntroducerDetail",
            format: "text",
          },
          {
            header: getTranslatedContent("{{PO_REFERENCE_NUMBER}}"),
            key: "PurchaseOrderRef",
            format: "text",
          },
          {
            header: getTranslatedContent("{{RN_REFERENCE}}"),
            key: "ReservationNo",
            format: "text",
          },
          {
            header: getTranslatedContent("{{REMARKS}}"),
            key: "Remarks",
            format: "text",
          },
          {
            header: getTranslatedContent("{{STATUS}}"),
            key: "SalesStatus",
            format: "text",
            formatter: (value) => orderStatusCode?.[value] ?? value ?? "",
          },
          {
            header: getTranslatedContent("{{TERMINATED}}"),
            key: "TerminatedForRefund",
            format: "text",
            formatter: (value) => value > 0 ? "Yes" : "No",
          },
        ];

        // Transform data to include index
        const dataWithIndex = orderData.map((order, index) => ({
          ...order,
          index: index + 1
        }));

        // Use the enhanced utility function to export CSV with formatting
        const result = exportToCSVWithFormatting(
          columnConfig,
          dataWithIndex,
          "monument_listing",
          "CSV export completed successfully"
        );

        if (result.success) {
          requestSuccess(result.message);
        } else {
          requestError(result.error);
        }
      } catch (error) {
        console.error("CSV export error:", error);
        requestError("Failed to generate CSV file");
      }
    };
    exportOrderListToCSVError = (error) => requestError(handleErrorMsg(error));

    getSelectedOrder = (order, type) =>
      Post(
        `/sales/getOrderDetails`,
        { salesID: order.SalesID },
        (payload) => this.getSelectedOrderSuccess(payload, type, order),
        this.getSelectedOrderError,
        this.load
      );
    getSelectedOrderSuccess = (payload, type, order) => {
      let tmp = {
        ...order,
        ...payload.Data?.[0],
        Subtotal: payload.Data?.MonumentPurchaseDetails?.[0].TotalAmount ?? 0,
        ...payload.Data?.MonumentPurchaseDetails?.[0],
        ...payload.Data?.MonumentPurchaseInstallment?.[0],
        TotalInstallmentAmount: payload.Data?.MonumentPurchaseInstallment?.[0].TotalAmount ?? 0
      };
      this.setState({
        selectedOrder: tmp,
        showDetailModal: type === "details",
        showAddAuthRepModal: type === "authRep",
        showAddSignatureModal: type === "digitalSignature"
      });
    };
    getSelectedOrderError = (error) => requestError(handleErrorMsg(error));

    closeModal = (type) => {
      switch (type) {
        case "showDetailModal":
          this.setState({ showDetailModal: false });
          break;
        case "showAddAuthRepModal":
          this.setState({ showAddAuthRepModal: false });
          break;
        case "showAddSignatureModal":
          this.setState({ showAddSignatureModal: false });
          break;
      }
    };

    addAuthorisedRep = (params) =>
      Post(
        `/sales/addAuthorizedRepresentative`,
        params,
        this.addAuthorisedRepSuccess,
        this.addAuthorisedRepError,
        this.load
      );
    addAuthorisedRepSuccess = (payload) => {
      requestSuccess(payload.Data)
      this.setState({ selectedOrder: {}, showAddAuthRepModal: false });
      this.getOrders(this.state.query);
    };
    addAuthorisedRepError = (error) => requestError(handleErrorMsg(error));

    addDigitalSignature = (base64) =>
      Post(
        `/sales/updateSalesSignature`,
        { salesID: this.state.selectedOrder.SalesID, signature: base64 },
        this.addDigitalSignatureSuccess,
        (error) => requestError(handleErrorMsg(error)),
        this.load
      );
    addDigitalSignatureSuccess = (payload) => {
      requestSuccess(payload.Data)
      this.setState({ selectedOrder: {}, showAddSignatureModal: false });
      this.getOrders(this.state.query);
    };

    getPurchaseOrders = (salesID) =>
      Post(
        "/form/getPurchaseOrder",
        { salesID: salesID },
        this.getPurchaseOrdersSuccess,
        this.getPurchaseOrdersError,
        this.load
      );
    getPurchaseOrdersSuccess = (payload) => {
      if (!payload?.Data?.details) {
        console.error("Missing details in API response:", payload);
        toast.error("Failed to generate the purchase order. Missing required details.");
        return;
      }
      // const details = payload.Data.details;
      // const items = payload.Data?.item ?? [];
      // const iu = payload.Data?.iu ?? [];
      const data = payload.Data.details;

      // Extract date without timezone conversion (backend already sends UTC+8)
      const salesDate = data.SalesDate ? data.SalesDate.split('T')[0] : "";

      const POData = {
        // PDFGenerator("/forms/PurchaseOrderForm.html", {
        poNo: data.SalesID,
        date: salesDate,

        purchaserFullname_asIC: data.Fullname,
        purchaserIC_NO: data.IC,
        purchaserNationality: data.Country_Nationality,
        dateOfBirth: data.DOB,
        purchaserPermanentAddress: data.ResidentialAddress,
        purchaserPscode: data.ResidentialPostCode,
        purchaserCorrsAddress: data.MailingAddress,
        purchaserCorrPscode: data.MailingPostCode,
        purchaser_mobilenum: data.MobileNumber,
        purchaserEmail: data.Email,
        purchaserReligion: data.Religion,
        purchaserDialectGrp: data.DialectGroup,
        purchaserMaritalStatus: data.MaritalStatus,
        purchaserMaritalStatusEN: data.MaritalStatus_EN,
        purchaserMaritalStatusZH: data.MaritalStatus_ZH,
        genderMale: data.Gender === "M" ? "checked" : "",
        genderFemale: data.Gender === "F" ? "checked" : "",

        // intendeduserFullname: payload.Data.iu[0]?.FullName ?? "",
        // intendeduserCNname: payload.Data.iu[0]?.ChineseName ?? "",
        // intendeduserNRIC: payload.Data.iu[0]?.IC ?? "",
        // intendeduserRelationship: payload.Data.iu[0]?.Relationship ?? "",
        // intendeduserGender: payload.Data.iu[0]?.Gender ?? "",

        // fspnName: payload.Data.details.ProductName,
        // fspCode: payload.Data.details.FSPCode,
        // fspPurchase: payload.Data.details?.UnitPrice ? `RM ${thousandSeperatorFormatter(payload.Data.details.UnitPrice)}` : "",
        // fspOthers: payload.Data.details?.MaintenanceFee ? `RM ${thousandSeperatorFormatter(payload.Data.details.MaintenanceFee)}` : "",
        // fspDiscount: "",
        totalPayment: data?.TotalPrice ? `RM ${thousandSeperatorFormatter(data.TotalPrice)}` : "",

        isFullPayment: data.IsFullPayment === 1 ? "checked" : "",
        isInstallment: data.IsFullPayment === 0 ? "checked" : "",
        months: "-",
        // cash: payload.Data.details.cash ? `RM ${thousandSeperatorFormatter(payload.Data.details.cash)}` : "",
        paymentbank: "",
        paymentOthers: "",
        paymenttotal: thousandSeperatorFormatter(data.PaidAmount),

        repName: "",
        repNRICNo: "",
        repContactNo: "",
        cert_collect_date: "",

        salespersonnel_name: data.Agent_Fullname,
        funeralDirector: "",
        salespersonnel_code: data.AgentID,
        salespersonnel_signature: "",

        officeUse_RandA: "",
        officeUse_CertNo: "",
        officeUse_UpdateBy: "",
        officeUse_Remarks: data.Remarks,

        unitID: data.UnitID,
        zone: data.ZoneX,
        row: data.RowX,
        hall: data.Hall,
        level: data.LevelX,
        plotSize: data.AreaSize,
        installment_period: data.InstallmentPeriod,
        isFullPayment: data.IsFullPayment === 1 ? "checked" : "",
        isInstallment: data.IsFullPayment !== 1 ? "checked" : "",

        isAsNeed: data.NeedCondition === 'AN' ? '&#10004' : '',
        isAsNeedEN: data.NeedCondition_EN === 'As-Need' ? '&#10004' : '',
        isAsNeedZH: data.NeedCondition_ZH === '按需' ? '&#10004' : '',
        isPreNeed: data.NeedCondition === 'PN' ? '&#10004' : '',
        isPreNeedEN: data.NeedCondition_EN === 'Pre-Need' ? '&#10004' : '',
        isPreNeedZH: data.NeedCondition_ZH === '预需' ? '&#10004' : '',

        // hasUrn: payload.Data?.item.length > 0 ? 'text-decoration: line-through;': '',
        // hasNoUrn: payload.Data?.item.length === 0 ? 'text-decoration: line-through;' : '',
        // UrnCode: payload.Data?.item?.[0]?.ProductCode ?? "",
        // UrnQty: payload.Data?.item?.[0]?.Quantity ?? "",

        signature: payload.Data?.details.Signature,
        poRemarks: payload.Data?.details.Remarks,

      };
      PDFGenerator(`/forms/PurchaseOrderForm.html`, POData, "legal");
    }

    getPurchaseOrdersError = (error) => {
      console.error("Error fetching addendum data:", error);
      if (error.response?.status === 401) {
        toast.error("Unauthorized access. Please login again.");
      } else {
        toast.error("Failed to fetch the addendum. Please try again.");
      }
    };

    // API: Update PO Reference Number
    updateSalesPORef = (newPORef) =>
      Post(
        "/sales/updateSalesPORef",
        { salesID: this.state.selectedOrder.SalesID, bindUnitSalesID: newPORef },
        this.updateSalesPORefSuccess,
        this.updateSalesPORefError,
        this.load
      );
    updateSalesPORefSuccess = (payload) => {
      requestSuccess(payload.Data);
      this.setState({ selectedOrder: { ...this.state.selectedOrder, PurchaseOrderRef: payload.Data.PurchaseOrderRef || payload.Data } });
    };
    updateSalesPORefError = (error) => requestError(handleErrorMsg(error));

    // API: Update Sales Remarks
    updateSalesRemarks = (newRemarks) =>
      Post(
        "/sales/updateSalesRemarks",
        { salesID: this.state.selectedOrder.SalesID, remarks: newRemarks },
        (payload) => this.updateSalesRemarksSuccess(payload, newRemarks),
        this.updateSalesRemarksError,
        this.load
      );
    updateSalesRemarksSuccess = (payload, newRemarks) => {
      requestSuccess(payload.Data);
      // Only update selectedOrder with the new remarks if API returns proper Remarks field
      // Otherwise keep the user's input
      this.setState({
        selectedOrder: {
          ...this.state.selectedOrder,
          Remarks: payload.Data?.Remarks || newRemarks
        }
      });
    };
    updateSalesRemarksError = (error) => requestError(handleErrorMsg(error));

    // API: Update Purchaser ID
    updateSalesPurchaserID = (newPurchaserID) =>
      Post(
        "/sales/updateSalesPurchaserID",
        { salesID: this.state.selectedOrder.SalesID, purchaserID: newPurchaserID },
        this.updateSalesPurchaserIDSuccess,
        this.updateSalesPurchaserIDError,
        this.load
      );
    updateSalesPurchaserIDSuccess = (payload) => {
      requestSuccess(payload.Data);
      // Update selectedOrder with the new purchaser ID
      this.setState({
        selectedOrder: {
          ...this.state.selectedOrder,
          PurchaserID: payload.Data.PurchaserID || payload.Data
        }
      });
    };
    updateSalesPurchaserIDError = (error) => requestError(handleErrorMsg(error));


    getPurchaseInvoice = (salesID) => {
      Post(
        "/form/getPurchaseOrder",
        { salesID },
        this.handlePurchaseInvoiceSuccess,
        this.handlePurchaseInvoiceError,
        this.load
      );
    };

    handlePurchaseInvoiceSuccess = (payload) => {
      if (!payload?.Data?.details) {
        console.error("Missing details in API response:", payload);
        toast.error("Failed to generate the purchase invoice. Missing required details.");
        return;
      }

      const details = payload.Data.details;
      const items = payload.Data?.item ?? [];
      const iu = payload.Data?.iu ?? [];

      // Extract date without timezone conversion (backend already sends UTC+8)
      const salesDateInvoice = details.SalesDate ? details.SalesDate.split('T')[0] : "";

      const invoiceData = {
        rbNO: details.SalesID,
        purchaseorder_date: salesDateInvoice,

        purchaserFullname_asIC: details.Fullname,
        purchaserIC_NO: details.IC,
        purchaserNationality: details.Country_Nationality,
        dateOfBirth: details.DOB,
        purchaserPermanentAddress: details.ResidentialAddress,
        purchaserPscode: details.ResidentialPostCode,
        purchaserCorrsAddress: details.MailingAddress,
        purchaserCorrPscode: details.MailingPostCode,
        purchaser_mobilenum: details.MobileNumber,
        purchaserEmail: details.Email,
        purchaserReligion: details.Religion,
        purchaserDialectGrp: details.DialectGroup,
        purchaserMaritalStatus: details.MaritalStatus,
        purchaserMaritalStatusEN: details.MaritalStatus_EN,
        purchaserMaritalStatusZH: details.MaritalStatus_ZH,
        genderMale: details.Gender === "M" ? "checked" : "",
        genderFemale: details.Gender === "F" ? "checked" : "",
        singleSize: details.UserCapacity === 1 ? "checked" : "",
        doubleSize: details.UserCapacity === 2 ? "checked" : "",
        otherSize: details.UserCapacity > 2 ? "checked" : "",

        intendeduserFullname: iu[0]?.FullName ?? "-",
        intendeduserCNname: iu[0]?.ChineseName ?? "-",
        intendeduserNRIC: iu[0]?.IC ?? "-",
        intendeduserRelationship: iu[0]?.Relationship ?? "-",
        intendeduserGender: iu[0]?.Gender ?? "-",

        intendeduserFullname2: iu[1]?.FullName ?? "-",
        intendeduserCNname2: iu[1]?.ChineseName ?? "-",
        intendeduserNRIC2: iu[1]?.IC ?? "-",
        intendeduserRelationship2: iu[1]?.Relationship ?? "-",
        intendeduserGender2: iu[1]?.Gender ?? "-",

        intendeduserFullname3: iu?.[2]?.FullName ?? "-",
        intendeduserCNname3: iu?.[2]?.ChineseName ?? "-",
        intendeduserNRIC3: iu?.[2]?.IC ?? "-",
        intendeduserRelationship3: iu?.[2]?.Relationship ?? "-",
        intendeduserGender3: iu?.[2]?.Gender ?? "-",

        intendeduserFullname4: iu?.[3]?.FullName ?? "-",
        intendeduserCNname4: iu?.[3]?.ChineseName ?? "-",
        intendeduserNRIC4: iu?.[3]?.IC ?? "-",
        intendeduserRelationship4: iu?.[3]?.Relationship ?? "-",
        intendeduserGender4: iu?.[3]?.Gender ?? "-",

        poRemarks: details.Remarks,
        plotZone: details.ZoneX,
        plotRow: details.RowX,
        plotLotno: details.UnitID,
        plotSize: details.AreaSize,
        plotPrice: thousandSeperatorFormatter(details.UnitPrice),
        plotMtn_Fclty: thousandSeperatorFormatter(details.MaintenanceFee),
        plotBckwall_walkwy: "",
        plotMonumentpckg: "",
        plotothers: "",
        plotTotal: thousandSeperatorFormatter(details.Total),
        plotTotalPrice: thousandSeperatorFormatter(details.TotalPrice),
        cash: "",
        paymentbank: "",
        paymentOthers: "",
        paymenttotal: thousandSeperatorFormatter(details.PaidAmount),

        repName: "",
        repIC: "",
        repContactNo: "",
        cert_collect_date: "",

        salespersonnel_name: details.Agent_Fullname,
        funeralDirector: "",
        salespersonnel_code: details.AgentID,
        salespersonnel_signature: "",

        officeUse_RandA: "",
        officeUse_CertNo: "",
        officeUse_UpdateBy: "",
        officeUse_Remarks: "",

        zone: details.ZoneX,
        hall: details.Hall,
        level: details.LevelX,
        installment_period: details.InstallmentPeriod,
        isFullPayment: details.IsFullPayment === 1 ? "checked" : "",
        isInstallment: details.IsFullPayment !== 1 ? "checked" : "",

        isAsNeed: details.NeedCondition === "AN" ? "&#10004;" : "",
        isAsNeedEN: details.NeedCondition_EN === "As-Need" ? "&#10004;" : "",
        isAsNeedZH: details.NeedCondition_ZH === "按需" ? "&#10004;" : "",
        isPreNeed: details.NeedCondition === "PN" ? "&#10004;" : "",
        isPreNeedEN: details.NeedCondition_EN === "Pre-Need" ? "&#10004;" : "",
        isPreNeedZH: details.NeedCondition_ZH === "预需" ? "&#10004;" : "",

        hasUrn: items.length > 0 ? "text-decoration: line-through;" : "",
        hasNoUrn: items.length === 0 ? "text-decoration: line-through;" : "",
        UrnCode: items[0]?.ProductCode ?? "",
        UrnQty: items[0]?.Quantity ?? "",

        signature: details.Signature,

        lotPO: details.LotPOReferenceNumber,

      };

      // Generate the PDF
      PDFGenerator(`/forms/PurchaseInvoice.html`, invoiceData, "legal");

    };

    handlePurchaseInvoiceError = (error) => {
      console.error("Error fetching purchase invoice:", error);
      if (error.response?.status === 401) {
        toast.error("Unauthorized access. Please login again.");
      } else {
        toast.error("Failed to fetch the purchase invoice. Please try again.");
      }
    };

    getAddendum = (salesID) =>
      Post("/form/getAddendum", { salesID: salesID }, this.getAddendumSuccess, this.getAddendumError, this.load);
    getAddendumSuccess = (payload) => {
      if (!Array.isArray(payload.Data) || payload.Data.length === 0) {
        return requestError("No addendum data found.");
      }

      const data = payload.Data[0]; // Extract the first item
      const installment = {
        TotalInstallments: data.Tenure,
        SumPerInstallment: data.ConstantInstallmentAmount,
        FirstInstallment: data.FirstInstallmentAmount
      };

      // Extract dates without timezone conversion (backend already sends UTC+8)
      const salesDate = data.SalesDate ? data.SalesDate.split('T')[0] : "";
      const installmentDueDate = data.FirstInstallmentDueDate ? data.FirstInstallmentDueDate.split('T')[0] : "";

      // Format dates to DD/MM/YYYY
      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };

      // Get day ordinal (1st, 2nd, 3rd, etc.)
      const getDayOrdinal = (dateStr) => {
        if (!dateStr) return "";
        const day = parseInt(dateStr.split('-')[2], 10);
        const suffixes = ["th", "st", "nd", "rd"];
        const v = day % 100;
        return day + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
      };

      PDFGenerator(`/forms/MonumentAddendum.html`, {
        purchaseOrderDate: formatDate(salesDate),
        name: data.Fullname,
        ICNo: data.IC,
        ProductName: data.ProductName,
        ProductCode: data.ProductCode ?? "-",
        plotNo: data.UnitID ?? "-",
        PONo: data.SalesID,
        months: installment.TotalInstallments,
        totalPurchasePrice: thousandSeperatorFormatter(data.Total),
        totaldownPayment: thousandSeperatorFormatter(data.Downpayment),
        totalInstallmentAmount: thousandSeperatorFormatter(
          (data.Tenure - 1) * data.ConstantInstallmentAmount + data.FirstInstallmentAmount
        ),

        totalMonths: installment.TotalInstallments,
        perMonthPayment: thousandSeperatorFormatter(installment.SumPerInstallment),
        installmentMonths: (installment.TotalInstallments ?? data.Tenure) - 1,
        firstPayment: thousandSeperatorFormatter(installment.FirstInstallment),
        fromDate: formatDate(installmentDueDate),
        everyDateOfMonth: getDayOrdinal(installmentDueDate),
        datePurchaser: formatDate(salesDate),
        signature: data.Signature,
        witName: "",
        witIC: "",
        witDate: "",
        recName: "",
        recDate: "",
        verName: "",
        verDate: "",
        appName: "",
        appDate: ""
      });
    };

    getAddendumError = (error) => requestError(handleErrorMsg(error));


    render = () => {
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
          onChangeHOC={this.onChangeHOC}
          getOrders={this.getOrders}
          getSelectedOrder={this.getSelectedOrder}
          addAuthorisedRep={this.addAuthorisedRep}
          getPurchaseOrders={this.getPurchaseOrders}
          getAddendum={this.getAddendum}
          getPurchaseInvoice={this.getPurchaseInvoice}
          addDigitalSignature={this.addDigitalSignature}
          updateSalesPORef={this.updateSalesPORef}
          updateSalesRemarks={this.updateSalesRemarks}
          updateSalesPurchaserID={this.updateSalesPurchaserID}
          exportOrderListToCSV={this.exportOrderListToCSV}
          closeModal={this.closeModal}
        />
      );
    };
  }

  const mapStateToProps = (state) => ({
    data: state,
    selectedLanguage: state.ProfileReducer.profile.preferred_language,
    profile: state.ProfileReducer.profile
  });
  return connect(mapStateToProps, { addLoadingContent, removeLoadingContent })(WithHOC);
};

export default HOC;
